"use client";

import Head from "next/head";
import { useRouter } from "next/router";
import { useUser } from "@/context/UserContext";
import { useProfile } from "@/context/ProfileContext";
import { useState, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import Joyride, { STATUS } from "react-joyride";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Button,
  Center,
  VStack,
  useToast,
  Flex,
  Spinner,
  Image,
  Portal,
  Icon,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";

import {
  FaMoneyBillWave,
  FaCalculator,
  FaUser,
  FaMoneyCheck,
  FaUserTie,
  FaBriefcase,
  FaCheckCircle,
  FaLock,
  FaArrowLeft,
  FaWhatsapp,
  FaLaughWink,
  FaHeadset,
  FaSyncAlt,
} from "react-icons/fa";

import GlassBox from "@/components/pressets/GlassBox";
import GlassButton from "@/components/pressets/GlassButton";
import GlassBackground from "@/components/pressets/GlassBackground";
import { getDeductionsTotal, getIncomeReport } from "@/api/incomeService";
import { getPurchaseReport } from "@/api/purchaseService";
import TaxToPay from "@/components/TaxToPay";
import { getPersonalData } from "@/api/personalDataService";
import GlassIconButton from "@/components/pressets/GlassIconButton";
import { HStack } from "@chakra-ui/react";
import QuickRegister from "@/components/QuickRegister";
import DataUpdateModalNoSync from "@/components/DataUpdateModalNoSync";

const buttonsData = [
  {
    name: "Datos personales",
    href: "/datos",
    description:
      "Gestionar y modificar datos personales como fecha de nacimiento, porcentaje de aportación y más.",
    icon: 1,
    color: "#9fcf5e",
    requiresAdmin: false,
    for: ["basic", "premium"],
    tourId: "personal-data-module",
    tourContent:
      "Este módulo es crucial. Aquí puedes configurar tu RUC y otros datos esenciales que impactan directamente el cálculo de impuestos. Asegúrate de que tu información esté siempre actualizada.",
  },
  {
    name: "Ingresos",
    href: "/ingresos",
    description: "Gestionar y registrar tus ingresos mensuales y anuales.",
    icon: 2,
    color: "#9fcf5e",
    requiresAdmin: false,
    for: ["premium"],
    tourId: "income-module",
    tourContent:
      "En el módulo de Ingresos, registra todos tus ingresos gravados y revisa tu proyección anual. Esto es vital para estimar correctamente tu Impuesto a la Renta. Aquí también verás el total de ingresos del año en curso.",
  },
  {
    name: "Deducciones",
    href: "/deducciones",
    description:
      "Administrar tus deducciones fiscales y documentos relacionados.",
    icon: 3,
    color: "#9fcf5e",
    requiresAdmin: false,
    for: ["premium"],
    tourId: "deductions-module",
    tourContent:
      "Utiliza esta sección para ingresar y gestionar tus deducciones personales, como seguro médico o intereses hipotecarios. Un buen registro de deducciones puede reducir significativamente tu impuesto a pagar. También verás el total deducido hasta el momento.",
  },
  {
    name: "Gastos personales",
    href: "/gastos",
    description:
      "Gestionar y registrar tus gastos y compras mensuales y anuales.",
    icon: 4,
    color: "#9fcf5e",
    requiresAdmin: false,
    for: ["basic", "premium"],
    tourId: "expenses-module",
    tourContent:
      "Este es el centro de control de tus gastos. Aquí se sincronizan tus facturas del SRI y puedes clasificarlas. El total que ves es el acumulado de compras para el mes actual.",
  },
];

const purchaseOptions = [
  {
    type: "basic",
    title: "Elaboración de anexo de gastos personales",
    icon: FaUserTie,
    features: [
      "Gestión de gastos personales",
      "Sincronización automática de compras con el SRI",
      "Generación de anexo de gastos personales",
      "Soporte básico",
    ],
    color: "green.400",
  },
  {
    type: "premium",
    title: "Trabajo bajo relación de dependencia",
    icon: FaBriefcase,
    features: [
      "Gestión de ingresos y proyección de ingresos",
      "Importación del Formulario 107 del SRI",
      "Gestión de deducciones",
      "Gestión de gastos personales",
      "Sincronización automática de compras con el SRI",
      "Soporte premium",
    ],
    color: "blue.400",
  },
];

const FloatingTaxButton = ({ accountType, showRucModal }) => {
  if (accountType !== "premium" || showRucModal) return null;

  const handleScroll = () => {
    const element = document.getElementById("tax-to-pay-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  };

  return (
    <Portal>
      <Box
        position="fixed"
        bottom={{ base: "16px", md: "24px" }}
        right={{ base: "16px", md: "24px" }}
        zIndex="9999"
        maxW={{ base: "calc(100vw - 32px)", md: "auto" }}
      >
        <Tooltip
          label="Cálculo de Impuesto a la Renta"
          placement="left"
          hasArrow
          bg="glass.solid"
          color="text.onGlass"
          fontSize="md"
          fontWeight="bold"
          px={4}
          py={2}
          borderRadius="lg"
          boxShadow="xl"
          border="1px solid"
          borderColor="blackAlpha.200"
        >
          <GlassIconButton
            colorScheme="green"
            onClick={handleScroll}
            icon={<Icon as={FaCalculator} />}
            size="xl"
            p={6}
            borderRadius="full"
          />
        </Tooltip>
      </Box>
    </Portal>
  );
};
const Home = () => {
  const router = useRouter();
  const { user } = useUser();
  const toast = useToast();
  const { isAdmin, accountType, setAccountType } = useUser();
  const { activeProfile } = useProfile();
  const [annualIncome, setAnnualIncome] = useState(null);
  const [loadingIncome, setLoadingIncome] = useState(true);
  const [yearlyExpenses, setYearlyExpenses] = useState(null);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [deductionsTotal, setDeductionsTotal] = useState(null);
  const [loadingDeductions, setLoadingDeductions] = useState(true);
  const [ruc, setRuc] = useState("");
  const [showPurchase, setShowPurchase] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: showPaymentModal,
    onOpen: openPaymentModal,
    onClose: closePaymentModal,
  } = useDisclosure();

  const [showRucModal, setShowRucModal] = useState(false);
  const [personalDataLoaded, setPersonalDataLoaded] = useState(false);
  const [needsPersonalData, setNeedsPersonalData] = useState(false);
  const [onboardingData, setOnboardingData] = useState({});

  const [runTour, setRunTour] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.4,
  });

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  const currentMonthName = monthNames[currentMonth - 1];

  useEffect(() => {
    const fetchBasicData = async () => {
      setPersonalDataLoaded(false);
      try {
        const data = await getPersonalData();
        // Keep accountType aligned with the active profile only. Writing the
        // default profile's plan here leaks module access into /perfiles.
        setAccountType(activeProfile?.accountType || "none");
        setRuc(data.ruc || "");
        setOnboardingData(data);
        setNeedsPersonalData(
          (activeProfile?.accountType === "basic" ||
            activeProfile?.accountType === "premium") &&
            !data.birthDate
        );
      } catch {
        setAccountType(activeProfile?.accountType || "none");
        setRuc(activeProfile?.ruc || "");
        setNeedsPersonalData(false);
      } finally {
        setPersonalDataLoaded(true);
      }
    };
    fetchBasicData();
  }, [activeProfile?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!accountType || accountType === "none") return;
    const fetchReports = async () => {
      try {
        const incomeData = await getIncomeReport(currentMonth > 2 ? currentYear : currentYear - 1);
        const totalIncome = (
          incomeData.sueldo +
          incomeData.comisiones +
          incomeData.utilidades +
          incomeData.bonos_mayores +
          incomeData.otros_gravados
        ).toFixed(2);
        setAnnualIncome(totalIncome);
      } catch {
        setAnnualIncome(0);
      } finally {
        setLoadingIncome(false);
      }
      try {
        const purchaseData = await getPurchaseReport(currentMonth > 2 ? currentYear : currentYear - 1);
        setYearlyExpenses(purchaseData.totalFinalAmount.toFixed(2));
      } catch {
        setYearlyExpenses(0);
      } finally {
        setLoadingExpenses(false);
      }
      try {
        const deductionsTotalData = await getDeductionsTotal(currentMonth > 2 ? currentYear : currentYear - 1);
        setDeductionsTotal(
          deductionsTotalData.totalInsuranceDeduction.toFixed(2),
        );
      } catch {
        setDeductionsTotal(0);
      } finally {
        setLoadingDeductions(false);
      }
    };
    fetchReports();
  }, [accountType, currentYear, currentMonth, activeProfile?._id]);

  const handleDataModalClose = () => {
    setShowRucModal(false);
    setNeedsPersonalData(false);
    onClose();
    if (accountType !== "none") {
      generateTourSteps();
      setRunTour(true);
    }
  };

  useEffect(() => {
    if (!personalDataLoaded) return;
    const isPaid =
      accountType === "basic" || accountType === "premium";

    if (isPaid && needsPersonalData) {
      setShowRucModal(true);
      onOpen();
      return;
    }

    setShowRucModal(false);
    onClose();

    if (isPaid && activeProfile?._id && !runTour) {
      const tourKey = `ctx-home-tour-complete:${activeProfile._id}`;
      if (localStorage.getItem(tourKey) !== "1") {
        generateTourSteps();
        setRunTour(true);
      }
    }
  }, [
    accountType,
    activeProfile?._id,
    needsPersonalData,
    personalDataLoaded,
    runTour,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNavigation = (href, name) => {
    toast({
      title: `Redirigiendo a ${name}...`,
      status: "info",
      duration: 1000,
      isClosable: true,
    });
    router.push(href);
  };

  const visibleButtons = buttonsData.filter(
    (btn) =>
      btn && (!btn.requiresAdmin || isAdmin) && btn.for.includes(accountType),
  );
  const columnsCount = visibleButtons.length < 4 ? visibleButtons.length : 2;

  const generateTourSteps = () => {
    const baseSteps = [
      {
        target: "body",
        content:
          "¡Bienvenido a ConTex! Vamos a dar un rápido recorrido por el menú principal, donde tendrás acceso a todas tus herramientas.",
        locale: { skip: "Saltar Tour", next: "Siguiente" },
        placement: "center",
      },
      {
        target: "#main-title-box",
        content:
          "Aquí verás el mensaje de bienvenida. Revisa siempre esta área para anuncios importantes.",
        placement: "bottom",
      },
    ];

    const moduleSteps = visibleButtons.map((btn) => ({
      target: `[data-tour="${btn.tourId}"]`,
      content: btn.tourContent,
      placement: "bottom-start",
      disableBeacon: true,
    }));

    let finalSteps = baseSteps.concat(moduleSteps);

    if (accountType === "premium") {
      finalSteps.push({
        target: '[data-tour="tax-to-pay-component-wrapper"]',
        content:
          "Este componente es el Estimador de Impuesto a Pagar. Muestra una proyección en tiempo real de tu Impuesto a la Renta basado en tus ingresos y deducciones registradas.",
        placement: "top",
        disableBeacon: true,
      });
    }

    finalSteps.push({
      target: "body",
      content:
        "¡Eso es todo! Ahora puedes empezar a gestionar tus datos y finanzas. Si tienes dudas, consulta la documentación.",
      placement: "center",
    });

    setTourSteps(finalSteps);
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRunTour(false);
      if (activeProfile?._id) {
        localStorage.setItem(
          `ctx-home-tour-complete:${activeProfile._id}`,
          "1"
        );
      }
    }
  };

  if (accountType === "none" && !showPurchase) {
    return (
      <>
        <Box
          as="nav"
          w="100%"
          bg="glass.strong"
          boxShadow="sm"
          py={{ base: 2, md: 3 }}
          px={{ base: 3, md: 6 }}
          position="fixed"
          top={0}
          left={0}
          zIndex={100}
        >
          <Flex justify="flex-end" align="center" maxW="7xl" mx="auto" w="100%">
            <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap" justify="flex-end">
              <GlassButton
                colorScheme="green"
                variant="ghost"
                py={{ base: 3, md: 4 }}
                px={{ base: 4, md: 8 }}
                onClick={() => {
                  const perfilSection = document.getElementById("cta-perfiles");
                  if (perfilSection) {
                    perfilSection.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }
                }}
              >
                Comprar
              </GlassButton>
              {!user && (
                <GlassButton
                  colorScheme="blue"
                  py={{ base: 3, md: 4 }}
                  px={{ base: 4, md: 8 }}
                  onClick={() => router.push("/login")}
                >
                  Iniciar sesión
                </GlassButton>
              )}
            </HStack>
          </Flex>
        </Box>
        <Box h={{ base: "56px", md: "60px" }} />
        <Box minH="100vh" bg="gray.700">
          <Head>
            <title>Bienvenido a ConTex | Optimiza tus Impuestos</title>
          </Head>
          <GlassBackground>
            <Center py={4} px={{ base: 0, md: 6 }}>
              <VStack align="center" maxW="5xl" spacing={{ base: 8, md: 12 }} w="full">
                {user && activeProfile && (
                  <GlassBox
                    variant="strong"
                    px={{ base: 4, md: 6 }}
                    py={4}
                    w="full"
                    display="flex"
                    flexDirection={{ base: "column", md: "row" }}
                    alignItems={{ base: "stretch", md: "center" }}
                    gap={3}
                  >
                    <Box flex="1" minW={0}>
                      <Text fontSize="sm" color="gray.500">
                        Perfil activo
                      </Text>
                      <Heading size="sm" noOfLines={1}>
                        {activeProfile.label || activeProfile.ruc || "Perfil"}
                        {activeProfile.ruc ? ` · ${activeProfile.ruc}` : ""}
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Todavía no tiene un plan activo. Elige uno para desbloquear
                        sus módulos.
                      </Text>
                    </Box>
                    <GlassButton
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      onClick={() => router.push("/perfiles")}
                    >
                      Cambiar de perfil
                    </GlassButton>
                  </GlassBox>
                )}
                {/* Hero Section */}
                <GlassBox
                  p={{ base: 5, md: 12 }}
                  w="full"
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={{ base: 4, md: 6 }}
                  mb={2}
                >
                  <Image
                    src="/assets/contex-logo-light.png"
                    alt="ConTex Logo"
                    w={{ base: "140px", md: "200px" }}
                    mb={4}
                  />
                  <Heading
                    size={{ base: "md", md: "lg" }}
                    color="brand.600"
                    fontWeight="extrabold"
                    lineHeight="1.1"
                  >
                    Optimiza tus impuestos y finanzas personales con ConTex
                  </Heading>
                  <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" maxW="700px">
                    La plataforma más fácil y segura para gestionar tus gastos,
                    ingresos y deducciones. Cumple con el SRI, ahorra tiempo y
                    dinero, y toma el control de tu declaración de impuestos.
                  </Text>
                  {/* Placeholder for a commercial hero image */}
                  <Box
                    w="100%"
                    maxW="600px"
                    h={{ base: "180px", md: "300px" }}
                    bgGradient="linear(to-br, green.100, green.300, blue.100)"
                    borderRadius="2xl"
                    boxShadow="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                    overflow="hidden"
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/iY3D4AYzPaE"
                      title="Video de presentación ConTex"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      autoPlay
                      style={{ borderRadius: "1.5rem" }}
                    ></iframe>
                  </Box>
                  <GlassButton
                    colorScheme="green"
                    size="lg"
                    fontWeight="bold"
                    fontSize="xl"
                    px={10}
                    py={7}
                    mt={2}
                    onClick={() => {
                      const perfilSection =
                        document.getElementById("cta-perfiles");
                      if (perfilSection) {
                        perfilSection.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                  >
                    ¡Comienza ahora!
                  </GlassButton>
                </GlassBox>
                <GlassBox
                  p={{ base: 6, md: 12 }}
                  w="full"
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={8}
                  bg="glass.strong"
                >
                  <Heading size="lg" color="brand.600" mb={2}>
                    ¿Por qué elegir ConTex?
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon as={FaLock} boxSize={12} color="green.400" />
                      <Heading size="md" color="brand.600">
                        Seguridad y privacidad
                      </Heading>
                      <Text color="gray.600">
                        Tus datos están protegidos con los más altos estándares
                        de seguridad y cifrado.
                      </Text>
                    </VStack>
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon as={FaCalculator} boxSize={12} color="blue.400" />
                      <Heading size="md" color="blue.600">
                        Cálculo automático de impuestos
                      </Heading>
                      <Text color="gray.600">
                        Calcula tu impuesto a la renta en tiempo real y evita
                        errores costosos.
                      </Text>
                    </VStack>
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon
                        as={FaMoneyBillWave}
                        boxSize={12}
                        color="green.400"
                      />
                      <Heading size="md" color="brand.600">
                        Ahorra tiempo y dinero
                      </Heading>
                      <Text color="gray.600">
                        Sincroniza tus comprobantes del SRI y
                        genera tu anexo de gastos personales en minutos.
                      </Text>
                    </VStack>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon as={FaLaughWink} boxSize={12} color="brand.600" />
                      <Heading size="md" color="brand.600">
                        Gestión fácil e intuitiva
                      </Heading>
                      <Text color="gray.600">
                        Interfaz amigable y soporte personalizado para que no te
                        pierdas en el proceso.
                      </Text>
                    </VStack>
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon as={FaHeadset} boxSize={12} color="blue.600" />
                      <Heading size="md" color="blue.600">
                        Soporte experto
                      </Heading>
                      <Text color="gray.600">
                        Nuestro equipo te acompaña en cada paso y responde todas
                        tus dudas.
                      </Text>
                    </VStack>
                    <VStack borderRadius="xl" py={4} px={2} bg="blackAlpha.50" spacing={4} align="center">
                      <Icon as={FaSyncAlt} boxSize={12} color="brand.600" />
                      <Heading size="md" color="brand.600">
                        Actualizaciones automáticas
                      </Heading>
                      <Text color="gray.600">
                        Siempre al día con los cambios del SRI y la normativa
                        tributaria.
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </GlassBox>

                {/* Call to Action: Profile Selection */}
                <GlassBox
                  p={{ base: 8, md: 12 }}
                  w="full"
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={6}
                  id="cta-perfiles"
                  scrollMarginTop="100px"
                >
                  <Heading size="lg" color="brand.600" mb={2}>
                    Elige tu plan y empieza a optimizar tus impuestos
                  </Heading>
                  <Text fontSize="lg" color="gray.600" mb={4}>
                    Selecciona la opción que mejor se adapte a tus necesidades:
                  </Text>
                  <SimpleGrid
                    columns={{ base: 1, md: 2 }}
                    spacing={8}
                    mt={4}
                    w="full"
                  >
                    <GlassBox
                      as={Button}
                      variant="ghost"
                      p={8}
                      h="full"
                      onClick={() => {
                        setSelectedType("basic");
                        setShowPurchase(true);
                      }}
                      _hover={{ shadow: "xl", transform: "scale(1.03)" }}
                    >
                      <VStack spacing={4}>
                        <Icon as={FaUserTie} boxSize={12} color="green.400" />
                        <Heading whiteSpace="pre-line" size="md">
                          Elaborar anexo de gastos personales
                        </Heading>
                        <Text
                          color="gray.500"
                          fontSize="md"
                          whiteSpace="pre-line"
                        >
                          Lleva el control de tus gastos personales y sincroniza
                          tus comprobantes electrónicos.
                        </Text>
                        <Flex align="center" justify="center" gap={2}>
                          <Text
                            color="brand.600"
                            fontSize="3xl"
                            whiteSpace="pre-line"
                            fontWeight="bold"
                          >
                            $10
                          </Text>
                          <Text
                            color="brand.600"
                            fontSize="lg"
                            whiteSpace="pre-line"
                            fontWeight="bold"
                          >
                            USD / año
                          </Text>
                        </Flex>
                      </VStack>
                    </GlassBox>
                    <GlassBox
                      as={Button}
                      variant="ghost"
                      p={8}
                      h="full"
                      onClick={() => {
                        setSelectedType("premium");
                        setShowPurchase(true);
                      }}
                      _hover={{ shadow: "xl", transform: "scale(1.03)" }}
                    >
                      <VStack spacing={4}>
                        <Icon as={FaBriefcase} boxSize={12} color="blue.400" />
                        <Heading whiteSpace="pre-line" size="md">
                          Trabajo bajo relación de dependencia
                        </Heading>
                        <Text
                          color="gray.500"
                          fontSize="md"
                          whiteSpace="pre-line"
                        >
                          Gestiona tus ingresos, deducciones y gastos personales
                          fácilmente.
                        </Text>
                        <Flex align="center" justify="center" gap={2}>
                          <Text
                            color="blue.600"
                            fontSize="3xl"
                            whiteSpace="pre-line"
                            fontWeight="bold"
                          >
                            $25
                          </Text>
                          <Text
                            color="blue.600"
                            fontSize="md"
                            whiteSpace="pre-line"
                            fontWeight="bold"
                          >
                            USD / año
                          </Text>
                        </Flex>
                      </VStack>
                    </GlassBox>
                  </SimpleGrid>
                  <Text
                    mt={6}
                    color="text.onGlassMuted"
                    fontSize="md"
                    whiteSpace="pre-line"
                  >
                    Un solo pago anual. IVA incluido.
                  </Text>
                </GlassBox>

                {/* Contact Section */}
                <GlassBox
                  p={{ base: 6, md: 10 }}
                  w="full"
                  textAlign="center"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={4}
                  bg="glass.strong"
                >
                  <Heading size="md" color="brand.600" mb={2}>
                    ¿Tienes preguntas o necesitas ayuda?
                  </Heading>
                  <Text color="gray.600" fontSize="lg">
                    Nuestro equipo de soporte está listo para ayudarte en todo
                    momento.
                  </Text>
                  <GlassButton
                    as="a"
                    href="https://wa.me/593981268239?text=Hola%20ConTex%2C%20tengo%20una%20consulta%20sobre%20la%20plataforma."
                    target="_blank"
                    rel="noopener noreferrer"
                    size="lg"
                    leftIcon={
                      <Icon
                        as={FaWhatsapp}
                        boxSize={6}
                        color="green.100"
                        mr={2}
                      />
                    }
                    colorScheme="green"
                    fontWeight="bold"
                    fontSize="lg"
                    py={6}
                    mt={2}
                  >
                    Contactar por WhatsApp
                  </GlassButton>
                </GlassBox>
              </VStack>
            </Center>
          </GlassBackground>
        </Box>
      </>
    );
  }

  if (showPurchase) {
    const option = purchaseOptions.find((o) => o.type === selectedType);
    const prices = {
      basic: {
        amount: 10,
        currency: "USD",
        period: "año",
      },
      premium: {
        amount: 25,
        currency: "USD",
        period: "año",
      },
    };
    const price = prices[selectedType];

    return (
      <Box minH="100vh" bg="gray.700">
        <Head>
          <title>Compra {option.title} | ConTex</title>
        </Head>
        <GlassBackground>
          <Center py={{ base: 4, md: 8 }} px={{ base: 0, md: 6 }}>
            <VStack align="center" maxW="3xl" spacing={{ base: 5, md: 8 }} w="full">
              <Flex
                as={GlassBox}
                p={{ base: 5, md: 10 }}
                px={{ base: 5, md: 12 }}
                w="100%"
                minW={0}
                textAlign="left"
                alignItems="stretch"
                gap={{ base: 6, md: 1 }}
                direction={{ base: "column", md: "row" }}
                justify="center"
              >
                <VStack
                  align="center"
                  minW={{ base: "100%", md: "280px" }}
                  maxW={{ md: "340px" }}
                  spacing={6}
                  flexShrink={0}
                  justify="center"
                  w="full"
                  mr={{ base: 0, md: 8 }}
                >
                  <Icon as={option.icon} boxSize={{ base: 14, md: 20 }} color={option.color} />
                  <Heading textAlign="center" size={{ base: "md", md: "lg" }}>
                    {option.title}
                  </Heading>
                  <Box
                    bg="glass.subtle"
                    borderRadius="xl"
                    px={{ base: 5, md: 8 }}
                    py={4}
                    boxShadow="md"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    w="full"
                  >
                    <Text fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={option.color}>
                      ${price.amount}
                      <Text
                        as="span"
                        fontSize="lg"
                        color="gray.600"
                        fontWeight="normal"
                      >
                        /{price.period}
                      </Text>
                    </Text>
                    <Text fontSize="md" color="gray.500">
                      IVA incluido. Un solo pago.
                    </Text>
                  </Box>
                  <GlassButton
                    py={6}
                    leftIcon={<Icon as={FaArrowLeft} boxSize={5} />}
                    colorScheme="gray"
                    variant="outline"
                    onClick={() => {
                      setShowPurchase(false);
                      setSelectedType(null);
                    }}
                  >
                    Regresar
                  </GlassButton>
                </VStack>
                <VStack
                  align="start"
                  spacing={6}
                  flex="1"
                  justify="center"
                  w="full"
                >
                  <Text fontSize="lg" color="gray.600">
                    ¡Obtén acceso a todas las herramientas que necesitas!
                  </Text>
                  <Text fontSize="lg" color="gray.600">
                    Características incluidas:
                  </Text>
                  <VStack spacing={3} align="start">
                    {option.features.map((f) => (
                      <Flex key={f} align="center" gap={2}>
                        <Icon as={FaCheckCircle} color={option.color} />
                        <Text>{f}</Text>
                      </Flex>
                    ))}
                  </VStack>
                  <VStack spacing={4} w="full" pt={2}>
                    <GlassButton
                      leftIcon={
                        <Icon
                          as={FaMoneyCheck}
                          boxSize={6}
                          color="green.100"
                          mr={2}
                        />
                      }
                      colorScheme="teal"
                      size="lg"
                      fontWeight="bold"
                      fontSize="lg"
                      py={6}
                      w="full"
                      onClick={() => {
                        setPaymentType("bank");
                        openPaymentModal();
                      }}
                    >
                      Comprar con Transferencia Bancaria
                    </GlassButton>
                    <GlassButton
                      leftIcon={
                        <Icon
                          as={FaMoneyBillWave}
                          boxSize={6}
                          color="blue.100"
                          mr={2}
                        />
                      }
                      colorScheme="blue"
                      size="lg"
                      fontWeight="bold"
                      fontSize="lg"
                      py={6}
                      w="full"
                      onClick={() => {
                        setPaymentType("card");
                        openPaymentModal();
                      }}
                    >
                      Comprar con Tarjeta de Crédito o Débito
                    </GlassButton>
                  </VStack>
                </VStack>
              </Flex>
              <VStack
                as={GlassBox}
                py={6}
                spacing={4}
                w="100%"
                textAlign="center"
              >
                <Text textAlign="center" fontSize="md" color="gray.500">
                  ¿Tienes dudas?{" "}
                  <b>¡Nuestro equipo de soporte está listo para ayudarte!</b>
                </Text>
                <GlassButton
                  as="a"
                  href="https://wa.me/593981268239?text=Hola%20ConTex%2C%20tengo%20una%20consulta%20sobre%20la%20compra%20de%20la%20plataforma."
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  leftIcon={
                    <Icon
                      as={FaWhatsapp}
                      boxSize={6}
                      color="green.100"
                      mr={2}
                    />
                  }
                  colorScheme="green"
                  fontWeight="bold"
                  fontSize="md"
                  py={6}
                >
                  Contactar por WhatsApp
                </GlassButton>
              </VStack>
            </VStack>
          </Center>
        </GlassBackground>
        <QuickRegister
          isOpen={showPaymentModal}
          onClose={closePaymentModal}
          paymentType={paymentType}
          purchaseType={selectedType}
        />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.700">
      <Head>
        <title>Menú Principal | ConTex</title>
      </Head>
      {tourSteps.length > 0 && (
        <Joyride
          run={runTour}
          steps={tourSteps}
          callback={handleJoyrideCallback}
          continuous
          showSkipButton
          styles={{
            options: {
              zIndex: 10000,
              primaryColor: "#9fcf5e",
            },
          }}
          locale={{
            back: "Atrás",
            close: "Cerrar",
            last: "Finalizar",
            next: "Siguiente",
            skip: "Saltar",
          }}
        />
      )}
      <GlassBackground>
        <Center py={4} px={{ base: 0, md: 6 }}>
          <VStack align="center" maxW="4xl" w="full">
            <GlassBox
              p={{ base: 5, md: 10 }}
              mb={4}
              w="full"
              textAlign="center"
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={4}
              id="main-title-box"
            >
              <Image
                src="/assets/contex-logo-light.png"
                alt="ConTex Logo"
                w={{ base: "160px", md: "220px" }}
              />
              <Text
                fontSize={{ base: "md", md: "xl" }}
                fontWeight="500"
                maxW="620px"
                lineHeight="1.35"
              >
                Selecciona una de las opciones para comenzar a trabajar:
              </Text>
            </GlassBox>
            <SimpleGrid
              columns={{ base: 1, md: columnsCount }}
              spacing={{ base: 5, md: 10 }}
              my={4}
              w="full"
            >
              {visibleButtons.map((btn) => (
                <GlassBox
                  key={btn.name}
                  variant="strong"
                  p={6}
                  spacing={4}
                  align="start"
                  h="full"
                  transition="all 0.3s"
                  cursor="pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNavigation(btn.href, btn.name)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleNavigation(btn.href, btn.name);
                    }
                  }}
                  _hover={{ shadow: "glassHover", transform: "translateY(-5px)" }}
                  _focusVisible={{ boxShadow: "focus" }}
                  data-tour={btn.tourId}
                >
                  <Flex gap={4} align="center" w="full" justify="space-between">
                    <Flex gap={4} align="center">
                      <Center
                        w={10}
                        h={10}
                        bg={btn.color}
                        color="white"
                        borderRadius="full"
                        mb={4}
                        boxShadow="md"
                      >
                        <Text fontSize="lg" fontWeight="bold">
                          {btn.icon}
                        </Text>
                      </Center>
                      <Heading mb={4} size="md" color="text.onGlass">
                        {btn.name}
                      </Heading>
                    </Flex>
                    {btn.name === "Ingresos" && (
                      <VStack align="flex-end" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm" color="text.onGlassMuted">
                          Total Año {currentMonth > 2 ? currentYear : currentYear - 1}
                        </Text>
                        <Text fontSize="xl" fontWeight="bold" color="brand.600">
                          {loadingIncome ? (
                            <Spinner size="sm" />
                          ) : annualIncome !== null ? (
                            `$${annualIncome}`
                          ) : (
                            "$0.00"
                          )}
                        </Text>
                      </VStack>
                    )}
                    {btn.name === "Gastos personales" && (
                      <VStack align="flex-end" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm" color="text.onGlassMuted">
                          Total Año {currentMonth > 2 ? currentYear : currentYear - 1}
                        </Text>
                        <Text fontSize="xl" fontWeight="bold" color="brand.600">
                          {loadingExpenses ? (
                            <Spinner size="sm" />
                          ) : yearlyExpenses !== null ? (
                            `$${yearlyExpenses}`
                          ) : (
                            "$0.00"
                          )}
                        </Text>
                      </VStack>
                    )}
                    {btn.name === "Deducciones" && (
                      <VStack align="flex-end" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm" color="text.onGlassMuted">
                          Total Año {currentMonth > 2 ? currentYear : currentYear - 1}
                        </Text>
                        <Text fontSize="xl" fontWeight="bold" color="brand.600">
                          {loadingDeductions ? (
                            <Spinner size="sm" />
                          ) : deductionsTotal !== null ? (
                            `$${deductionsTotal}`
                          ) : (
                            "$0.00"
                          )}
                        </Text>
                      </VStack>
                    )}
                  </Flex>
                  <Text>{btn.description}</Text>
                  <GlassButton
                    mt={4}
                    colorScheme={btn.color}
                    size="sm"
                    p={5}
                    w="full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigation(btn.href, btn.name);
                    }}
                  >
                    Ir a {btn.name}
                  </GlassButton>
                </GlassBox>
              ))}
            </SimpleGrid>
            <Box
              id="tax-to-pay-section"
              ref={ref}
              minH="100px"
              w="full"
              data-tour="tax-to-pay-component-wrapper"
              style={{ scrollMarginTop: "20px" }}
            >
              {accountType === "premium" && inView && <TaxToPay />}
            </Box>
          </VStack>
        </Center>
      </GlassBackground>

      {showRucModal && (
        <DataUpdateModalNoSync
          key={activeProfile?._id || "onboarding"}
          isOpen
          onClose={handleDataModalClose}
          initialData={onboardingData}
          skipIdentification
        />
      )}
      <FloatingTaxButton
        accountType={accountType}
        showRucModal={showRucModal}
      />
    </Box>
  );
};

export default Home;
